import React, { useState, useRef, useEffect } from 'react';
import { useLabels, useRequestData } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { usePopper } from 'react-popper';
import { setClassNames } from '@ddc/classnames-js';
import Tooltip from './Tooltip';
import { HIDE_SHOW_PAYMENTS_TOOLTIP_KEY } from '../global-constants';
import { setHideTooltip, getHideTooltip } from '../utilities/wiapiSettings';

const Switch = ({ isOn, handleToggle, label, newMobileUI = false }) => {
	const elementRef = useRef(null);
	const popperRef = useRef(null);
	const [arrowRef, setArrowRef] = useState(null);
	const { windowId } = useRequestData();

	// Delay load the tooltip to allow for the MyWallet integration and correct attachment to the switch.
	const [isTooltipDelayed, setIsTooltipDelayed] = useState(true);
	useEffect(() => {
		const wiapiLoadedTimer = setTimeout(() => {
			setIsTooltipDelayed(false);
		}, 3000);
		return () => clearTimeout(wiapiLoadedTimer);
	}, []);

	const [isTooltipShown, setIsTooltipShown] = useState(
		!window.sessionStorage.getItem(HIDE_SHOW_PAYMENTS_TOOLTIP_KEY)
	);

	const labels = useLabels();

	const { styles, attributes } = usePopper(
		elementRef.current,
		popperRef.current,
		{
			modifiers: [
				{
					name: 'arrow',
					options: {
						element: arrowRef
					}
				},
				{
					name: 'offset',
					options: {
						offset: [4, 12]
					}
				}
			],
			placement: 'bottom-end'
		}
	);

	const handleNotNow = () => {
		setIsTooltipShown(false);
		setHideTooltip();
	};

	const notNowLinkColor =
		window?.DDC?.dataLayer?.site?.design?.themekit?.includes('DARK')
			? 'text-link'
			: 'text-link text-link-override';

	const handlePersonalizePayments = () => {
		setIsTooltipShown(false);
		setHideTooltip();
		handleToggle(true, true);
	};

	const switchId = `switch-${windowId}`;

	return (
		<>
			<label
				className={`switch-container border-radius-md pointer d-inline-flex mb-2 ${
					newMobileUI ? 'ddc-font-size-xsmall' : 'px-3'
				}`}
				htmlFor={switchId}
			>
				<span className={`switch-label ${isOn ? '' : 'text-muted'}`}>
					{label}
				</span>
				<input
					id={switchId}
					type="checkbox"
					role="switch"
					checked={isOn}
					onChange={handleToggle}
					className="switch-checkbox sr-only"
					tabIndex={0}
					aria-checked={isOn}
				/>
				<span
					className={setClassNames([
						isOn
							? 'switch-slider active'
							: 'switch-slider inactive',
						newMobileUI ? 'ml-2' : ''
					])}
					ref={elementRef}
					aria-hidden="true"
				>
					<span className="switch-toogle" />
				</span>
			</label>
			<Tooltip
				popperRef={popperRef}
				styles={styles}
				attributes={attributes}
				setArrowRef={setArrowRef}
				isShown={
					!isOn &&
					isTooltipShown &&
					!getHideTooltip() &&
					!isTooltipDelayed
				}
			>
				<p className="text-body">
					<strong>{labels.get('NEW_EXCLAMATION')}</strong>{' '}
					{labels.get('CUSTOMIZE_YOUR_PAYMENTS_VERBIAGE')}
				</p>
				<div className="d-flex align-items-center">
					<button
						type="button"
						className={`ml-auto ${notNowLinkColor}`}
						onClick={handleNotNow}
					>
						{labels.get('NOT_NOW')}
					</button>
					<button
						type="button"
						className="btn btn-primary ml-4"
						onClick={handlePersonalizePayments}
					>
						{labels.get('PERSONALIZE_PAYMENTS')}
					</button>
				</div>
			</Tooltip>
		</>
	);
};

Switch.propTypes = {
	isOn: PropTypes.bool,
	handleToggle: PropTypes.func,
	label: PropTypes.string,
	newMobileUI: PropTypes.bool
};

export default Switch;
